import React, { useCallback, useEffect, useState } from "react";
import { useChecks } from "../../Hooks/useChecks";
import { formatCurrency, formatNumber, money, toast, validarPINSeguridad } from "../../../../utils/helpers";
import { apiRequest } from "../../../../hooks/apiRequest";
import { auth } from "../../../../utils/auth";
import {
	normalizarUnidades,
	unidadBase,
	construirOpcionUnidad,
	opcionUnidadBase,
	escalarPreciosPorUnidad,
	cantidadMaximaEnUnidad,
	aCantidadBase,
	precioPorUnidad,
	filaUnidadDeLinea,
	reetiquetarPrecio,
	usaPrecioPaquete,
	describirCantidad,
} from "../../Hooks/useUnidadLinea";

const calcularDescuento = (costo, descuento) =>
	money(costo * descuento / 100);

// El impuesto de una línea llega del backend como {id, nombre}, pero
// `updateTotal` lo reescribe con la forma del dropdown ({value, label}).
// Cuando `changeProductoToEdit` vuelve a correr sobre esa línea ya reescrita,
// leer sólo `id`/`nombre` dejaba el impuesto vacío.
const aOpcionImpuesto = (impuesto) => ({
	label: impuesto?.nombre ?? impuesto?.label,
	value: impuesto?.id ?? impuesto?.value,
	porcentaje: impuesto?.porcentaje,
	tipo: impuesto?.tipo,
});

// `precio` de la lista lo calcula el API sin redondear (2086.21 × 1.16 =
// 2420.0036), pero `precio_facturado` se guarda a 2 decimales: compararlos
// con === nunca encontraba la lista y la línea caía en precio manual.
const mismoMonto = (a, b) => Math.abs(parseFloat(a) - parseFloat(b)) < 0.005;

const useProducto =(producto, facturaProductos, updateTotal, index, impuestosForDropdown, banderaContacto, precioLimite, contacto, isEdit, isRecovery, fromPlantilla) => {

    const [productoInfo, setProductoInfo] = useState({
		id: false,
		nombre: false,
		cantidad: producto.cantidad,
		costo: producto.costo,
		descuento: producto.descuento,
        importe: producto.importe,
        precio_facturado: producto.precio_facturado,
		precios: [],
		impuesto: aOpcionImpuesto(producto.impuesto),
		precio_venta: null,
		existencia: null,
		cantidadAnterior: null,
		precioCompra: null,
		unidades: [],
		unidadSeleccionada: null,
		factor: 1,
	});

	const { checkProductoRepetidoFactura, checkExistenciaProducto } = useChecks(
        facturaProductos,
		productoInfo
	);

	const [showFotos, setShowFotos] = useState(false);
	const [showDelete, setShowDelete] = useState(false);
	const [showPrecioManual, setShowPrecioManual] = useState(false);
	const [showPINModal, setShowPINModal] = useState(false);
    const [showPrecioCompra, setShowPrecioCompra] = useState(false);

	const [pinSeguridad, setPinSeguridad] = useState("");
	const [precioManual, setPrecioManual] = useState(0);
	const [pinSeguridadValidated, setPinSeguridadValidated] = useState(false);
    const [chequeoPrecio, setChequeoPrecio] = useState(true);

    const [isPrecioPorDebajo, setIsPrecioPorDebajo] = useState(false);

	const onChangeInput = (field, value) => {
		setProductoInfo((producto) => ({
			...producto,
			[field]: value,
		}));
	};

	const handlePrecioVentaChange = (precio) => {
		if (precio.value === "manual") {
			setShowPrecioManual(true);
			return;
		}

        const imp = impuestosForDropdown.find(i => i.value === precio.impuesto_id);

		setProductoInfo(prev => ({
			...prev,
			impuesto: imp,
			costo: calcularImporte(precio.value, imp),
			precio_venta: precio,
			descuento: 0,
		}));

        setPrecioManual(precio.value);
	};

    const onChangeImpuesto = (impuesto_id) => {
        const imp = impuestosForDropdown.find(i => i.value === impuesto_id);
        const precio = (productoInfo.costo * parseFloat(imp?.porcentaje + 1)).toFixed(2);

        setProductoInfo(prev => ({
            ...prev,
            precio_venta: {
                value: precio,
                label: `1 - ${formatNumber(precio)}`,
                impuesto_id: imp.id,
            },
            impuesto: imp,
        }));

        setPrecioManual(precio);
    }

    // En precio manual el usuario teclea el precio con impuesto incluido, así
    // que al cambiar el impuesto se conserva ese precio y se recalcula el costo.
    const onChangeImpuestoManual = (impuesto_id) => {
        const imp = impuestosForDropdown.find(i => i.value === impuesto_id);
        if (!imp) return;

        setProductoInfo(prev => ({
            ...prev,
            impuesto: imp,
            costo: calcularImporte(precioManual || 0, imp),
        }));
    }

	const onChangeProducto = (productoId, newProducto = {}) => {
		if (!checkExistenciaProducto(newProducto)) return;
		checkProductoRepetidoFactura(productoId, index);

		producto.producto_id = productoId;
		producto.productoSeleccionado = null;

        setShowPrecioManual(false);
        setPrecioManual('');

        let plength = precioLimite && newProducto?.precios.length >= precioLimite ? precioLimite : (precioLimite && precioLimite > newProducto?.precios.length ? newProducto?.precios.length : 1);
        plength = banderaContacto ? plength : 1;

        updateProducto(newProducto, plength - 1);
	};

	const productLabel = useCallback((producto) => {
		return (
			<span className="row pl-0 pr-5 align-items-center">
				<span
					className="col-2 pr-1"
					style={{ minWidth: "80px", wordBreak: "break-all", fontSize: '12px' }}
				>
					{producto.referencia}
				</span>
				<span className="col px-1" style={{fontSize: '12px'}}>{producto.nombre}</span>
				<span
					className="col-1 px-1"
					style={{
						fontWeight: "bold",
						color: "#008aab",
                        fontSize: '12px',
					}}
				>
					{isNaN(parseFloat(producto.cantidad))
						? "∞"
						: parseFloat(producto.cantidad)}
				</span>
				<span
					className="col-2 pr-0"
					style={{
						fontWeight: "bold",
						color: "#008aab",
                        fontSize: '12px',
					}}
				>
					{banderaContacto &&
						precioLimite >= 1 &&
						producto.precios?.length >= precioLimite
							? formatCurrency(
									money   (
										producto.precios[precioLimite - 1]
											?.total
									) || 0
							  )
							: formatCurrency(
									money(producto.precios[0]?.total) || 0
							  )}
				</span>

                <span
					className="col-2 pr-0"
					style={{
						fontWeight: "bold",
						color: "#008aab",
                        fontSize: '12px',
					}}
				>
                    {producto.ubicacion?.nombre}
                </span>
			</span>
		);
	}, [contacto]);

	const productLabelMobile = useCallback((producto) => {
		const precio = banderaContacto &&
			precioLimite >= 1 &&
			producto.precios?.length >= precioLimite
				? formatCurrency(money(producto.precios[precioLimite - 1]?.total) || 0)
				: formatCurrency(money(producto.precios[0]?.total) || 0);

		return (
			<span className="d-flex flex-column py-1" style={{ fontSize: "16px", lineHeight: 1.3 }}>
                <span className="d-flex justify-content-between align-items-start">
					<span style={{ whiteSpace: "normal", wordBreak: "break-word", flex: 1, paddingRight: "6px", fontWeight: "600" }}>
						{producto.nombre}
					</span>
					<span style={{ fontWeight: "bold", whiteSpace: "nowrap", width: "60px", textAlign: "right" }}>
						{isNaN(parseFloat(producto.cantidad))
							? "∞"
							: parseFloat(producto.cantidad)} <small>{producto.unidadSeleccionada?.label || producto.unidad}</small>
					</span>
				</span>
				<span className="d-flex justify-content-between">
					<span>
						{producto.referencia}
					</span>
					<span style={{ fontWeight: "bold", color: "#008aab", whiteSpace: "nowrap" }}>
						{precio}
					</span>
				</span>
                {producto.ubicacion?.nombre && (
                    <span style={{ color: "#888888", fontSize: "11px" }}>
                        {producto.ubicacion.nombre}
                    </span>
                )}
			</span>
		);
	}, [banderaContacto, precioLimite]);

	const calcularItbis = (total) => {
		return total * (productoInfo.impuesto?.porcentaje || 0);
	};

    const volverAlSelector = async (noActualizarCosto = false) => {
        const plength = banderaContacto && precioLimite ? precioLimite : 1;
        const precioOriginal = productoInfo?.precios[plength-1];
        const precioEnUnidad = precioPorUnidad(precioOriginal?.total, productoInfo.unidadSeleccionada, precioOriginal?.precio_paquete);
        setShowPrecioManual(false);

        handlePrecioVentaChange({
            label: `${plength} -> ${formatNumber(precioEnUnidad)}`,
            value: precioEnUnidad,
            valorBase: parseFloat(precioOriginal?.total),
            precio_paquete: precioOriginal?.precio_paquete ?? null,
            impuesto_id: precioOriginal.impuesto?.id,
            tipo: precioOriginal.impuesto?.tipo
        });

        setProductoInfo(prev => {
            const newProductoInfo = {
                ...prev,
                impuesto: {
                    label: precioOriginal?.impuesto?.nombre,
                    value: precioOriginal?.impuesto?.id,
                    porcentaje: precioOriginal?.impuesto?.porcentaje,
                    tipo: precioOriginal?.impuesto?.tipo,
                },
                precio_manual: precioEnUnidad
            }

            onChangeCostoManual(precioEnUnidad, false, newProductoInfo);

            return newProductoInfo;
        });
    }

	const preciosVentaBase = useCallback(() => {
		if (!precioLimite && !banderaContacto) {
			return productoInfo.precios?.map((precio, index) => ({
				value: parseFloat(precio.total),
				label: `${index + 1} -> ${formatNumber(precio.total)}`,
				impuesto_id: precio.impuesto?.id,
					precio_paquete: precio.precio_paquete ?? null,
			}));
		}

		let precios = [];

		if (productoInfo.precios?.length > 0 && !banderaContacto) {
            productoInfo.precios.forEach((precio, index) => {

				if (index + 1 <= precioLimite) {
					precios.push({
						value: parseFloat(precio.total),
						label: `${index + 1} -> ${formatNumber(precio.total)}`,
						impuesto_id: precio.impuesto?.id,
					precio_paquete: precio.precio_paquete ?? null,
					});
				}
			});
		} else if (productoInfo.precios?.length > 0 && banderaContacto) {
            if (precioLimite > productoInfo.precios?.length) {
                productoInfo.precios.forEach((precio, index) => {
                    if (index + 1 === productoInfo.precios.length) {
                        precios.push({
                            value: parseFloat(precio.total),
                            label: `${index + 1} -> ${formatNumber(precio.total)}`,
                            impuesto_id: precio.impuesto?.id,
					precio_paquete: precio.precio_paquete ?? null,
                        });
                    }
                });
            } else {
                productoInfo.precios.forEach((precio, index) => {
                    if (index + 1 >= precioLimite) {
                        precios.push({
                            value: parseFloat(precio.total),
                            label: `${index + 1} -> ${formatNumber(precio.total)}`,
                            impuesto_id: precio.impuesto?.id,
					precio_paquete: precio.precio_paquete ?? null,
                        });
                    }
                });
            }
		}
		return precios;
	}, [productoInfo.precios, precioLimite, banderaContacto]);

	// Cada opción de precio escalada por la unidad de la línea (factor × precio
	// de lista, o el precio propio de la unidad si lo tiene). `valorBase` queda
	// disponible para re-escalar sin arrastrar el factor anterior.
	const preciosVenta = useCallback(() => {
		return escalarPreciosPorUnidad(preciosVentaBase(), productoInfo.unidadSeleccionada);
	}, [preciosVentaBase, productoInfo.unidadSeleccionada]);

	const onChangeUnidad = (nuevaUnidad) => {
		setProductoInfo((prev) => {
			const siguiente = {
				...prev,
				unidadSeleccionada: nuevaUnidad,
				factor: nuevaUnidad?.factor ?? 1,
			};

			// El precio manual lo escribió el usuario en la unidad anterior; no
			// se re-escala solo, para no cambiarle a la fuerza lo que tecleó.
			if (!showPrecioManual && prev.precio_venta?.valorBase != null) {
				const nuevoValor = precioPorUnidad(prev.precio_venta.valorBase, nuevaUnidad, prev.precio_venta.precio_paquete);
				siguiente.precio_venta = {
					...prev.precio_venta,
					value: nuevoValor,
					label: reetiquetarPrecio(prev.precio_venta.label, nuevoValor),
				};
				siguiente.costo = calcularImporte(nuevoValor, prev.impuesto);
			}

			return siguiente;
		});
	};

	const toggleFotosModal = () => {
		setShowFotos((state) => !state);
	};

	const toggleDeleteModal = () => {
		setShowDelete((state) => !state);
	};

	const onChangeCostoManual = async (value, revisarCalculo = false, productoOriginal = null) => {
        const precios = preciosVenta().length > 0 ? preciosVenta() : productoOriginal?.precios;
        const descuento = productoInfo.descuento ? value * productoInfo.descuento / 100 : 0;

        let plength = (precioLimite && precios.length >= precioLimite) ? precioLimite : precios.length;
        plength = banderaContacto ? precios.length : plength;
        const precioVenta = precios[plength-1];
        const precioMinimo = Math.min(...precios.map(p => p.value));

        let imp = null;

        if ( productoOriginal ) {
            imp = productoOriginal.impuesto;
        } else if (showPrecioManual ) {
            imp = productoInfo.impuesto;
        } else {
            imp = impuestosForDropdown.find(
                i => i.value === precioVenta?.impuesto_id
            );
        }

		const importeCalculado = calcularImporte(value || precioManual, imp);

        if (revisarCalculo && importeCalculado - descuento < (productoInfo.precioCompra || 0) * (productoInfo.factor || 1)) {
            toast("ATENCIÓN: Este precio es menor al costo de compra.", "error");
        }

		if (revisarCalculo && value - descuento < precioMinimo) {
            if (pinSeguridadValidated) {
				onChangeInput("costo", importeCalculado);
				return;
			}

            setIsPrecioPorDebajo(true);

			setShowPINModal(true);
			return;
		}

        onChangeInput("costo", importeCalculado);
	};

	const productoITBIS = calcularItbis(productoInfo.costo);

    const st = !showPrecioManual
		? ((productoInfo?.precio_venta?.value?.toFixed(2)) * productoInfo.cantidad) ||
		  0
		: precioManual * productoInfo.cantidad;

	// const totalItbis = calcularItbis(costoTotal);
	const totalItbis =
    calcularItbis(productoInfo.costo).toFixed(2) * productoInfo.cantidad;

	const costoTotal = st - totalItbis;

	const desc =
		calcularDescuento(costoTotal + totalItbis, productoInfo.descuento) || 0;

	const subtotal = costoTotal || 0;

    const costoReal = (productoInfo.costo - (productoInfo.costo * productoInfo.descuento / 100)) * productoInfo.cantidad;
    const descuentoReal = calcularDescuento(productoInfo.costo * productoInfo.cantidad, productoInfo.descuento);
    const itbisReal = calcularItbis(costoReal);
    const totalReal = costoReal + itbisReal;

	const total = st;

    //Cargar la informacion del producto desde factura existente o autoguardado
	const changeProductoToEdit = (productoOriginal = {}) => {
        const productoId = productoOriginal.producto.id;
        const precio_facturado = productoOriginal.precio_facturado;

		if (!productoId) {
            setShowPrecioManual(true);
            onChangeCostoManual(precio_facturado);
            setPrecioManual(precio_facturado);
            return;
        }

        const newProducto = productoOriginal.producto;

        // La línea trae el snapshot de su unidad: `unidad_id` desde el
        // autoguardado, o sólo `unidad` (nombre) + `factor` desde el backend,
        // que no guarda la FK en el detalle. Sin coincidencia cae en la unidad
        // base y el comportamiento es idéntico al de antes de la Fase 2.
        const filasUnidad = normalizarUnidades(newProducto);
        const filaUnidadOriginal = filaUnidadDeLinea(filasUnidad, productoOriginal);
        const opcionUnidadOriginal = filaUnidadOriginal
            ? construirOpcionUnidad(filaUnidadOriginal)
            : null;
        // El precio se guardó con el factor del snapshot, no con el actual.
        const factorOriginal = Number(productoOriginal.factor) || opcionUnidadOriginal?.factor || 1;

        // El precio guardado está en la unidad de la línea. Para ubicar cuál
        // lista de precio coincide se compara contra su precio de paquete si la
        // línea es de empaque y la lista lo tiene; si no, contra el precio de
        // la lista, volviendo a la unidad base con el factor del snapshot.
        let precioIndex = 0;
        precioIndex = newProducto.precios?.findIndex((precio) =>
            usaPrecioPaquete(opcionUnidadOriginal, precio.precio_paquete)
                ? mismoMonto(precio.precio_paquete, precio_facturado)
                : mismoMonto(precio.precio, precio_facturado / factorOriginal)
        );

        if (precioIndex < 0) {
            setShowPrecioManual(true);
            updateProducto(newProducto, precioIndex, productoOriginal, opcionUnidadOriginal);
            setPrecioManual(precio_facturado);

            //Checkeo de precios al cargar
            if(
                isRecovery
                && chequeoPrecio
                && precio_facturado > 0
                && !isEdit
                && !fromPlantilla
            ) {
                onChangeCostoManual(precio_facturado, true, productoOriginal);
                setChequeoPrecio(false);
            }
            return;
        }

        updateProducto(newProducto, precioIndex, productoOriginal, opcionUnidadOriginal);
	};

    const updateProducto = (newProducto, precioIndex, productoOriginal = null, unidadOverride = null) => {
        let newPrecio = null;

        if (precioIndex >= 0) {
            newPrecio = newProducto?.precios[precioIndex];
        }

        // Antes del fallback: sólo un precio de lista viene en unidad base y se
        // escala; el fallback (precio_facturado de la línea) ya está en la
        // unidad de la línea y escalarlo otra vez inflaría costo e ITBIS.
        const precioDeLista = Boolean(newPrecio);

        //Usar el impuesto del primer precio de venta
        let newimpuesto = {};

        if (productoOriginal) {
            newimpuesto = productoOriginal.impuesto;
        } else {
            newimpuesto = newProducto.precios ? newProducto.precios[0]?.impuesto : newProducto.impuesto;
        }

        if (!newPrecio) {
            newPrecio = {
                precio: productoInfo.precio_facturado,
                precio_venta: calcularImporte(productoInfo.precio_facturado, newimpuesto),
                impuesto: newimpuesto
            };
        }

        const filasUnidad = normalizarUnidades(newProducto);
        const opcionUnidad = unidadOverride || opcionUnidadBase(newProducto);
        const precioEscalado = precioDeLista
            ? precioPorUnidad(parseFloat(newPrecio?.precio || 0), opcionUnidad, newPrecio?.precio_paquete)
            : parseFloat(newPrecio?.precio || 0);
        // El precio de paquete de la lista trae impuesto incluido: el costo se
        // deriva de él, no de precio_venta × factor.
        const costoEscalado = !precioDeLista
            ? newPrecio?.precio_venta
            : usaPrecioPaquete(opcionUnidad, newPrecio?.precio_paquete)
                ? calcularImporte(precioEscalado, newPrecio?.impuesto)
                : precioPorUnidad(newPrecio?.precio_venta, opcionUnidad);

        setProductoInfo((state) => ({
            ...state,
            id: newProducto.id,
            producto: newProducto,
            nombre: (
                <>
                    <span
                        className="col-3 pl-1"
                        style={{
                            minWidth: "100px",
                            wordBreak: "break-all !important",
                        }}
                    >
                        {newProducto.referencia}
                    </span>{" "}
                    <span className="col-8">{newProducto.nombre}</span>
                </>
            ) || '',
            importe: newProducto.importe,
            costo: costoEscalado,
            precio_facturado: newProducto.precio_facturado || newPrecio.precio,
            referencia: newProducto.referencia,
            unidad: opcionUnidad.value,
            unidades: filasUnidad,
            unidadSeleccionada: opcionUnidad,
            factor: opcionUnidad.factor,
            precios: newProducto.precios || [],
            fotos: newProducto.fotos
                ? newProducto.fotos.map((foto) => foto.url)
                : [],
            precio_venta: {
                value: precioEscalado,
                valorBase: precioDeLista ? parseFloat(newPrecio?.precio || 0) : null,
                precio_paquete: precioDeLista ? newPrecio?.precio_paquete ?? null : null,
                label: `${precioIndex + 1} -> ${formatNumber(precioEscalado)}`,
                impuesto_id: aOpcionImpuesto(newPrecio.impuesto).value,
            },
            impuesto: aOpcionImpuesto(newPrecio.impuesto),
            existencia: newProducto.cantidad,
            // Al editar, la existencia del almacén ya viene descontada por esta
            // misma línea, así que `cantidadAnterior` es lo que la línea ya tomó
            // y se le suma de vuelta. Va en unidad base, como la existencia: el
            // snapshot `cantidad_base` del backend, o reconstruido con el factor
            // guardado para las líneas anteriores a la Fase 2. Sumar la cantidad
            // cruda devolvía 2 donde la línea había tomado 2 CAJAS de 12.
            cantidadAnterior: isEdit
                ? producto.cantidad_base ?? aCantidadBase(producto.cantidad, producto.factor ?? 1)
                : newProducto.cantidad,
            precioCompra: newProducto.costo,
        }));
    }

	const checkQuantityConstrains = () => {
        if (!productoInfo.producto?.inventariable) return;

        const factor = productoInfo.factor || 1;
        const cantidadEnBase = aCantidadBase(productoInfo.cantidad, factor);
        const unidadLinea = productoInfo.unidadSeleccionada;
        // Todo lo que se compara aquí está en unidad base, pero el usuario
        // tecleó en la unidad de la línea: los mensajes dicen ambas.
        const describir = (cantidad) =>
            describirCantidad(cantidad, productoInfo.producto, unidadLinea);
        const avisarEmpaqueIncompleto = (existencia) => {
            if (factor <= 1) return;
            toast(
                `No hay una ${unidadLinea?.value} completa disponible: quedan ${describir(existencia)} y una ${unidadLinea?.value} son ${factor} ${unidadBase(productoInfo.producto)?.unidad || "UND"}`,
                "error"
            );
        };

        if (isEdit) {
            const existencia = productoInfo.existencia + productoInfo.cantidadAnterior;
			if (
				(productoInfo.existencia != null &&
				productoInfo.cantidadAnterior != null &&
				cantidadEnBase > existencia) || existencia < 1
			) {
				const maximo = cantidadMaximaEnUnidad(existencia, factor);
				toast(
					`Existencia Actual ${describir(existencia)}. Solicitado ${describir(cantidadEnBase)}`,
					"error"
				);
				if (maximo <= 0) {
					avisarEmpaqueIncompleto(existencia);
				}
				onChangeInput(
					"cantidad",
					maximo
				);
                return;
			}
            if (productoInfo.cantidadAnterior) {
                return;
            }
		}

        if (
			(productoInfo.existencia != null &&
			cantidadEnBase > productoInfo.existencia) || productoInfo.existencia <= 0
		) {
			const maximo = cantidadMaximaEnUnidad(productoInfo.existencia, factor);
			toast(
				`Existencia Actual ${describir(productoInfo.existencia)}. Solicitado ${describir(cantidadEnBase)}`,
				"error"
			);
			if (maximo <= 0) {
				avisarEmpaqueIncompleto(productoInfo.existencia);
			}
			producto.cantidad = maximo;
			onChangeInput("cantidad", maximo);
			return;
		}

		if (producto.cantidad <= 0) {
			toast(`Cantidad Debe ser mayor a 0`, "error");
			producto.cantidad = 1;
			onChangeInput("cantidad", 1);
			return;
		}
	};

    const getProductos = async (url) => {
        const table = await apiRequest({}, url, 'get');
        if (table.data) {
            return table.data.filter((producto) => {
                if (!auth.esVendedor()) return true;
                return auth.almacenes().includes(producto.almacen?.id);
            }).map(item => {
                return {
                    value: item.id,
                    label: item.nombre,
                    producto: item
                };
            });
        } else {
            return [{label: "No se encontraron productos", value: null}];
        }
    };

	const checkPINSeguridad = async () => {
		if (await validarPINSeguridad(pinSeguridad)) {
            proceedWithPin();
			return;
		}

		toast("PIN incorrecto, intente de nuevo", "error");
	};

    const proceedWithPin = (proceed = false) => {
        if (!proceed.detail) return;

        setPinSeguridad("");
        setShowPINModal(false);
        setPinSeguridadValidated(true);
    }

	const togglePINModal = () => {
		setShowPINModal(false);

        if (isPrecioPorDebajo) {
            const plength = banderaContacto && precioLimite ? precioLimite : 1;

            const precioLista = productoInfo.precios[plength-1];
            const nuevoPrecio = productoInfo.precio_venta?.value || precioPorUnidad(money(precioLista?.precio), productoInfo.unidadSeleccionada, precioLista?.precio_paquete);
            const importeCalculado =  calcularImporte(nuevoPrecio, productoInfo.impuesto);

            onChangeInput("costo", importeCalculado);
            onChangeInput("descuento", 0);
            setPrecioManual(nuevoPrecio);

            volverAlSelector();
        }
	};

    const calcularImporte = (precio, impuesto) => {
        return parseFloat(precio) / (parseFloat(impuesto?.porcentaje) + 1);
    }

    const onChangeDescuento = (descuento) => {
        if (descuento === 0) return;

		const precioVentaMin = Math.min(...preciosVenta().map(p => p.value));

        if (
            totalReal < (precioVentaMin * productoInfo.cantidad || 0) ||
            descuento < 0
        ) {
			toast("Este descuento no puede ser aplicado", "error");
            setIsPrecioPorDebajo(true);
            setShowPINModal(true);
		}
    }


	useEffect(() => {
		if (!producto.producto?.id ||!producto.precio_facturado) {
            return;
        }
        changeProductoToEdit(
            producto
        );
	}, [producto.producto?.id]);

    //Esto Actualiza la cantidad luego de chekear productos duplicados
	useEffect(() => {
		onChangeInput("cantidad", producto.cantidad);
	}, [producto.cantidad]);

	useEffect(() => {
		updateTotal(
			producto.id,
			desc > 0 ? costoReal : subtotal,
			desc > 0 ? totalReal : total,
			desc > 0 ? itbisReal : totalItbis,
			desc > 0 ? descuentoReal : 0,
			productoInfo.impuesto,
            productoInfo
		);
	}, [subtotal, totalItbis, total, desc, showPrecioManual, productoInfo.unidadSeleccionada]);

	useEffect(() => {
		onChangeDescuento(productoInfo.descuento);
	}, [productoInfo.descuento]);

    useEffect(() => {
        window.addEventListener('pinValidado', proceedWithPin);
        window.addEventListener('pinCancelado', togglePINModal);
        return () => {
            window.removeEventListener('pinValidado', proceedWithPin);
            window.removeEventListener('pinValidado', togglePINModal);
        }
    }, [togglePINModal, proceedWithPin]);

    return {
        productoInfo,
        productLabel,
        productLabelMobile,
        getProductos,
        total,
        totalReal,
        preciosVenta,
        precioManual,
        pinSeguridad,
        productoITBIS,
        checkQuantityConstrains,
        checkPINSeguridad,
        onChangeInput,
        onChangeImpuesto,
        onChangeImpuestoManual,
        onChangeProducto,
        onChangeCostoManual,
        onChangeDescuento,
        onChangeUnidad,
        handlePrecioVentaChange,
        setProductoInfo,
        setPrecioManual,
        setPinSeguridad,
        setShowPrecioManual,
        setShowFotos,
        toggleFotosModal,
        toggleDeleteModal,
        togglePINModal,
        showPrecioManual,
        showPrecioCompra,
        setShowPrecioCompra,
        showFotos,
        showDelete,
        showPINModal,
        volverAlSelector
    }
}

export default useProducto;
